// Client HTTP du front. Toutes les requêtes passent par /api (Pages Function `functions/api`),
// qui ajoute le Bearer côté serveur : le navigateur ne voit jamais SEC_TOKEN.
//
// Règles du contrat 0.14.1 appliquées ici :
//  - Idempotency-Key unique sur chaque mutation, réutilisée seulement pour rejouer la même tentative
//    après une erreur réseau (même requête, même clé).
//  - If-Match : ETag de la dernière lecture de la ressource ; à défaut, `"<version>"` (REQ-38).
//  - 304 sur GET /state avec If-None-Match.
//  - Erreurs dans l'enveloppe { error: { code, message, details } }.
//  - 409 avec confirmation_token + choices → ConfirmationRequiredError, rejoué avec une nouvelle clé.
import type { ConfirmationChoice, ErrorDetails } from './types';

export const API_BASE = '/api';
export const SUPPORTED_API_MINOR = '0.14';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** 409 qui demande un choix explicite (§13bis « Confirmations »). */
export class ConfirmationRequiredError extends ApiError {
  readonly token: string;
  readonly choices: ConfirmationChoice[];
  readonly expiresAt: string | null;
  readonly info: ErrorDetails;
  constructor(status: number, code: string, message: string, details: ErrorDetails) {
    super(status, code, message, details);
    this.name = 'ConfirmationRequiredError';
    this.token = details.confirmation_token ?? '';
    this.choices = details.choices ?? [];
    this.expiresAt = details.expires_at ?? null;
    this.info = details;
  }
}

export class NetworkError extends Error {
  constructor(message = 'Service injoignable') {
    super(message);
    this.name = 'NetworkError';
  }
}

export interface Confirmation {
  token: string;
  choice: ConfirmationChoice;
}

export interface RequestOptions {
  query?: Record<string, string | number | boolean | null | undefined>;
  body?: unknown;
  /** Valeur de version (sans guillemets) ou ETag complet ; requis pour les mutations versionnées. */
  ifMatch?: string | null;
  confirmation?: Confirmation;
  signal?: AbortSignal;
}

export interface ApiResponse<T> {
  data: T;
  status: number;
  etag: string | null;
}

// ETag par chemin de ressource (sans query), alimenté par chaque réponse qui en porte un.
const etags = new Map<string, string>();
// Dernière réponse 200 de GET /state par URL complète, pour servir les 304.
const conditionalCache = new Map<string, { etag: string; data: unknown }>();

let mutationsBlocked: string | null = null;
/** Bloque les mutations si la version mineure de l'API n'est pas celle attendue (REQ-19). */
export function blockMutations(reason: string | null) {
  mutationsBlocked = reason;
}

function normalizeEtag(tag: string): string {
  const t = tag.trim().replace(/^W\//, '');
  return t.startsWith('"') ? t : `"${t}"`;
}

/** If-Match à envoyer pour une ressource : ETag mémorisé si la version correspond, sinon `"version"`. */
export function ifMatchFor(path: string, version?: string | null): string | null {
  const known = etags.get(path);
  if (version) {
    const v = normalizeEtag(version);
    if (known && normalizeEtag(known) === v) return known.replace(/^W\//, '');
    return v;
  }
  return known ? known.replace(/^W\//, '') : null;
}

export function rememberEtag(path: string, etag: string | null | undefined) {
  if (etag) etags.set(path, etag);
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const qs = new URLSearchParams();
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      qs.set(k, String(v));
    }
  }
  const s = qs.toString();
  return `${API_BASE}${path}${s ? `?${s}` : ''}`;
}

function newKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

async function parseError(res: Response): Promise<ApiError> {
  let code = `HTTP_${res.status}`;
  let message = res.statusText || 'Erreur';
  let details: unknown;
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string; details?: unknown } };
    if (body?.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
      details = body.error.details;
    }
  } catch {
    /* corps vide ou non JSON */
  }
  const d = details as ErrorDetails | undefined;
  if (res.status === 409 && d && typeof d === 'object' && !Array.isArray(d) && d.confirmation_token) {
    return new ConfirmationRequiredError(res.status, code, message, d);
  }
  return new ApiError(res.status, code, message, details);
}

export async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<ApiResponse<T>> {
  const isMutation = method !== 'GET' && method !== 'HEAD';
  if (isMutation && mutationsBlocked) {
    throw new ApiError(0, 'API_VERSION_UNSUPPORTED', mutationsBlocked);
  }
  const url = buildUrl(path, opts.query);
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (isMutation) headers['Idempotency-Key'] = newKey();
  if (opts.ifMatch) headers['If-Match'] = normalizeEtag(opts.ifMatch);
  if (opts.confirmation) {
    headers['X-Confirmation-Token'] = opts.confirmation.token;
    headers['X-Confirmation-Choice'] = opts.confirmation.choice;
  }
  const cached = method === 'GET' && path === '/state' ? conditionalCache.get(url) : undefined;
  if (cached) headers['If-None-Match'] = cached.etag;

  const init: RequestInit = {
    method,
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
    credentials: 'same-origin',
  };

  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (e) {
    if ((e as Error)?.name === 'AbortError') throw e;
    // Une seule nouvelle tentative, avec la même Idempotency-Key : le serveur rejoue le résultat
    // si la première requête avait abouti.
    try {
      res = await fetch(url, init);
    } catch {
      throw new NetworkError();
    }
  }

  const etag = res.headers.get('ETag');
  if (res.status === 304 && cached) {
    return { data: cached.data as T, status: 200, etag: cached.etag };
  }
  if (!res.ok) throw await parseError(res);

  const resourcePath = path.split('?')[0];
  if (etag) rememberEtag(resourcePath, etag);

  let data: unknown = null;
  if (res.status !== 204) {
    const text = await res.text();
    data = text ? JSON.parse(text) : null;
  }
  if (method === 'GET' && path === '/state' && etag) conditionalCache.set(url, { etag, data });
  return { data: data as T, status: res.status, etag };
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    request<T>('GET', path, { query, signal }).then((r) => r.data),
  send: <T>(method: 'POST' | 'PATCH' | 'PUT' | 'DELETE', path: string, opts: RequestOptions = {}) => request<T>(method, path, opts),
};

/** Récupère toutes les pages d'une collection (limite 100 par page). */
export async function getAll<T>(path: string, query: RequestOptions['query'] = {}, limit = 100): Promise<T[]> {
  const out: T[] = [];
  for (let page = 1; page < 50; page++) {
    const res = await api.get<{ data: T[]; total: number }>(path, { ...query, page, limit });
    out.push(...res.data);
    if (out.length >= res.total || res.data.length === 0) break;
  }
  return out;
}
